import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { fetchAnalysisResult } from "../../../api/aiReviewService";
import { AIReview } from '../../../common/models/types';
import { WaitingManager } from '../../../components/organisms/CommonUI/WaitingPopup/WaitingManager';
import AdminAIReviewCompareTemplate from '../../../components/templates/AdminAIReviewCompareTemplate';

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 18px;
  color: #666;
`;

const AdminAIReviewComparePopup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const leftReviewId = searchParams.get("left");
  const rightReviewId = searchParams.get("right");
  
  const [leftReview, setLeftReview] = useState<AIReview | null>(null);
  const [rightReview, setRightReview] = useState<AIReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReviews = async () => {
      if (!leftReviewId || !rightReviewId) {
        setError('리뷰 ID가 없습니다.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        WaitingManager.show({ message: 'AI 리뷰 정보를 불러오는 중...' });
        
        // 두 리뷰를 병렬로 로드
        const [leftResponse, rightResponse] = await Promise.all([
          fetchAnalysisResult(leftReviewId),
          fetchAnalysisResult(rightReviewId)
        ]);
        
        if (leftResponse.error === 0 && leftResponse.data) {
          setLeftReview(leftResponse.data);
        } else {
          setError(leftResponse.message || '왼쪽 리뷰를 불러올 수 없습니다.');
          return;
        }
        
        if (rightResponse.error === 0 && rightResponse.data) {
          setRightReview(rightResponse.data);
        } else {
          setError(rightResponse.message || '오른쪽 리뷰를 불러올 수 없습니다.');
          return;
        }
      } catch (err) {
        console.error('Failed to load reviews:', err);
        setError('리뷰를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
        WaitingManager.hide();
      }
    };

    loadReviews();
  }, [leftReviewId, rightReviewId]);

  // 팝업 창 타이틀 설정
  useEffect(() => {
    if (leftReview && rightReview) {
      document.title = `AI Review 비교 #${leftReview.id} vs #${rightReview.id}`;
    }
  }, [leftReview, rightReview]);

  if (loading) {
    return null; // WaitingManager가 표시됨
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  if (!leftReview || !rightReview) {
    return <ErrorContainer>리뷰 정보가 없습니다.</ErrorContainer>;
  }

  return <AdminAIReviewCompareTemplate leftReview={leftReview} rightReview={rightReview} />;
};

export default AdminAIReviewComparePopup;